import type { TProjectState } from "@/types/project-state";

export type TProjectStateGroup = TProjectState["group"];

export const PROJECT_STATE_GROUPS: readonly TProjectStateGroup[] = [
  "backlog",
  "unstarted",
  "started",
  "completed",
  "cancelled",
] as const;

/**
 * Project state groups that mean "done with it".
 *
 * The frontend counterpart of the backend's ``CLOSED_PROJECT_STATE_GROUPS``, and the
 * one owner of the pair here: it was written out separately in the program overview,
 * the plan-work Gantt and the capacity risk strip, which is three places to forget
 * when a sixth group appears.
 *
 * `lib/overdue.ts` keeps its own copy on purpose — it takes a bare group string and
 * answers for work items as well as projects, so it is not this question.
 */
export const CLOSED_PROJECT_STATE_GROUPS: ReadonlySet<TProjectStateGroup> = new Set([
  "completed",
  "cancelled",
]);

/**
 * Whether a project whose state is `stateId` is done with.
 *
 * A project with no state, or one whose state is not in the bank the caller passes,
 * reads as OPEN. That is the safe default: the bank is fetched lazily (NG-980), so a
 * caller that asks before it lands should show the project rather than hide it.
 */
export function isClosedProjectState(
  stateId: string | null | undefined,
  stateById: ReadonlyMap<string, { group: TProjectStateGroup }>,
): boolean {
  if (!stateId) return false;
  const group = stateById.get(stateId)?.group;
  return group != null && CLOSED_PROJECT_STATE_GROUPS.has(group);
}

/**
 * Order project states the way Workspace Settings shows them: by state group
 * (backlog → unstarted → started → completed → cancelled), then by each state's
 * `sequence` within the group. Mirrors `getGroupedStates` (the settings page)
 * and the work-item board's `orderWorkspaceStatesByGroup`. The stored `order`
 * field isn't group-sorted, so the Projects board read out of sync with
 * Settings until this pass was applied (#3186).
 */
export const orderProjectStatesByGroup = <
  T extends { group: TProjectStateGroup; sequence: number },
>(
  states: T[],
): T[] =>
  PROJECT_STATE_GROUPS.flatMap((group) =>
    states.filter((s) => s.group === group).sort((a, b) => a.sequence - b.sequence),
  );

export const WORKSPACE_PROJECT_STATE_GROUP_LABEL_KEYS: Record<TProjectStateGroup, string> = {
  backlog: "workspace_settings.project_states.group.backlog",
  unstarted: "workspace_settings.project_states.group.unstarted",
  started: "workspace_settings.project_states.group.started",
  completed: "workspace_settings.project_states.group.completed",
  cancelled: "workspace_settings.project_states.group.cancelled",
};
